import {
  ApiUseCasesListEuAiActRiskClassEnum,
  DashboardUseCaseCounts,
  DashboardUseCaseModelCount,
  MaterialityEnum,
} from '@api';
import { MapperHelper } from '@helpers/mapper';
import { notionStore } from '@stores/notion.store';
import { AppRoutes } from '@types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Legend,
  Tooltip,
  ChartData,
  ChartOptions,
  ScriptableContext,
} from 'chart.js';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import { ChartCard } from '../ChartCard/ChartCard';
import { StackedPill } from '../StackedPill/StackedPill';

import styles from './Portfolio.module.scss';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, Tooltip, Legend);

function lineChartLabel(context: any) {
  return `${context.parsed.y} use case${context.parsed.y !== 1 ? 's' : ''}`;
}

type BorderRadiusType =
  | number
  | { topLeft: number; bottomLeft: number; topRight: number; bottomRight: number }
  | { topLeft: number; bottomLeft: number }
  | { topRight: number; bottomRight: number };

interface DatasetType {
  data: number[];
  backgroundColor: string;
  hoverBackgroundColor: string;
  borderRadius?: BorderRadiusType;
  borderSkipped?: boolean;
  datasetIndex?: number;
}

const data: ChartData<'line'> = {
  datasets: [
    {
      label: 'Use case',
      data: [2, 4, 11, 9, 6, 10, 14, 21, 22, 29, 33, 37],
      borderWidth: 3,
      tension: 0.4,
      borderColor: (context: ScriptableContext<'line'>) => {
        const { ctx } = context.chart;
        const gradient = ctx.createLinearGradient(0, 0, 0, 156);
        gradient.addColorStop(1, '#aacbe9');
        gradient.addColorStop(0, '#3d5a80');
        return gradient;
      },
      pointRadius: 2,
    },
  ],
};

const options: ChartOptions<'line'> = {
  layout: {
    padding: 0,
  },
  devicePixelRatio: 2.5,
  plugins: {
    title: {
      display: false,
    },
    legend: {
      display: false,
    },
    datalabels: {
      display: false,
    },
    tooltip: {
      backgroundColor: '#3d5a80d4',
      displayColors: true,
      boxPadding: 4,
      padding: 8,
      bodyFont: {
        size: 14,
      },
      callbacks: {
        label: lineChartLabel,
      },
    },
  },
  responsive: true,
  maintainAspectRatio: false,
  datasets: {
    line: {},
  },
  scales: {
    x: {
      display: false,
    },
    y: {
      display: false,
    },
  },
};

interface PortfolioProps extends Omit<DashboardUseCaseCounts, 'riskAssessment'> {
  useCasesTotal: number;
  useCases: DashboardUseCaseModelCount[];
  liveModels: number;
  inReviewModels: number;
  notLiveModels: number;
}

export const Portfolio: React.FC<PortfolioProps> = observer(
  ({
    useCasesTotal,
    useCases,
    liveModels,
    inReviewModels,
    notLiveModels,
    euAiActRiskClass,
    materiality,
    readiness,
  }) => {
    const { values } = notionStore;

    const { push } = useRouter();

    const modelsData = useMemo(() => {
      const datasets: DatasetType[] = [];
      datasets[0] = {
        data: [liveModels],
        backgroundColor: '#5d9f46e6',
        hoverBackgroundColor: '#5d9f46cc',
        borderSkipped: false,
        datasetIndex: 0,
      };

      datasets[1] = {
        data: [inReviewModels],
        backgroundColor: '#ddab49e6',
        hoverBackgroundColor: '#ddab49cc',
        borderSkipped: false,
        datasetIndex: 1,
      };

      datasets[2] = {
        data: [notLiveModels],
        backgroundColor: '#b7bcc8e6',
        hoverBackgroundColor: '#b7bcc8cc',
        borderSkipped: false,
        datasetIndex: 2,
      };

      const riskValues = [liveModels, inReviewModels, notLiveModels];
      const borderRadiuses = MapperHelper.applyBorderRadius(riskValues);
      const [liveBorderRadius, inReviewBorderRadius, notLiveBorderRadius] = borderRadiuses;
      datasets[0].borderRadius = liveBorderRadius;
      datasets[1].borderRadius = inReviewBorderRadius;
      datasets[2].borderRadius = notLiveBorderRadius;
      const total = datasets.reduce((acc, curr) => acc + curr.data[0], 0);

      return {
        labels: ['Models'],
        datasets: datasets
          .map(v => ({ ...v, data: [((v.data[0] / total) * 10000) / 100], rawData: v.data[0] }))
          .filter(x => x.data[0] > 0),
      };
    }, [liveModels, inReviewModels, notLiveModels]);

    const euAIActRiskData = useMemo(() => {
      const datasets: DatasetType[] = [];
      datasets[0] = {
        data: [euAiActRiskClass.minimalRisk],
        datasetIndex: 0,
        backgroundColor: '#5d9f46e6',
        hoverBackgroundColor: '#5d9f46cc',
        borderSkipped: false,
      };

      datasets[1] = {
        data: [euAiActRiskClass.limitedRisk],
        backgroundColor: '#e3ba16',
        hoverBackgroundColor: '#d1a245',
        borderSkipped: false,
        datasetIndex: 1,
      };

      datasets[2] = {
        data: [euAiActRiskClass.highRisk],
        backgroundColor: '#e77e23e6',
        hoverBackgroundColor: '#e77e23cc',
        borderSkipped: false,
        datasetIndex: 1,
      };

      datasets[3] = {
        data: [euAiActRiskClass.prohibitedVeryHighRisk],
        backgroundColor: '#c84c3ce6',
        hoverBackgroundColor: '#c84c3ccc',
        borderSkipped: false,
        datasetIndex: 2,
      };

      datasets[4] = {
        data: [euAiActRiskClass.toBeAssessed],
        backgroundColor: '#b7bcc8e6',
        hoverBackgroundColor: '#b7bcc8cc',
        borderSkipped: false,
        datasetIndex: 3,
      };

      const riskValues = [
        euAiActRiskClass.minimalRisk,
        euAiActRiskClass.limitedRisk,
        euAiActRiskClass.highRisk,
        euAiActRiskClass.prohibitedVeryHighRisk,
        euAiActRiskClass.toBeAssessed,
      ];
      const borderRadiuses = MapperHelper.applyBorderRadius(riskValues);
      const [
        minimalRiskBorderRadius,
        limitedRiskBorderRadius,
        highRiskBorderRadius,
        prohibitedRiskBorderRadius,
        toBeAssessedBorderRadius,
      ] = borderRadiuses;
      datasets[0].borderRadius = minimalRiskBorderRadius;
      datasets[1].borderRadius = limitedRiskBorderRadius;
      datasets[2].borderRadius = highRiskBorderRadius;
      datasets[3].borderRadius = prohibitedRiskBorderRadius;
      datasets[4].borderRadius = toBeAssessedBorderRadius;
      const total = riskValues.reduce((acc, curr) => acc + curr, 0);

      return {
        labels: ['EU AI Act Risk Classification'],
        datasets: datasets
          .map(v => ({ ...v, data: [((v.data[0] / total) * 10000) / 100], rawData: v.data[0] }))
          .filter(x => x.data[0] > 0),
      };
    }, [euAiActRiskClass]);

    // Create Lifecycle/Readiness chart data
    const readinessData = useMemo(() => {
      const datasets: DatasetType[] = [];
      const borderRadiuses = MapperHelper.applyBorderRadius(Object.values(readiness));

      // Get all lifecycle states except 'total'
      const lifecycleStates = Object.keys(readiness).filter(key => key !== 'total');

      // Generate shades of blue dynamically
      const generateBlueShades = (count: number) => {
        const baseColors = {
          darkest: { r: 61, g: 90, b: 128 }, // #3d5a80
          lightest: { r: 137, g: 180, b: 216 }, // #89b4d8
        };

        const shades: { [key: string]: string } = {};

        lifecycleStates.forEach((state, index) => {
          const ratio = count === 1 ? 0 : index / (count - 1);
          const r = Math.round(baseColors.darkest.r + (baseColors.lightest.r - baseColors.darkest.r) * ratio);
          const g = Math.round(baseColors.darkest.g + (baseColors.lightest.g - baseColors.darkest.g) * ratio);
          const b = Math.round(baseColors.darkest.b + (baseColors.lightest.b - baseColors.darkest.b) * ratio);

          shades[state] = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
            .toString(16)
            .padStart(2, '0')}e6`;
        });

        return shades;
      };

      const blueShades = generateBlueShades(lifecycleStates.length);

      Object.entries(readiness).forEach(([key, value], index) => {
        if (key !== 'total') {
          datasets.push({
            data: [value],
            backgroundColor: blueShades[key],
            hoverBackgroundColor: blueShades[key].replace('e6', 'cc'),
            borderSkipped: false,
            borderRadius: borderRadiuses[index],
            datasetIndex: index,
          });
        }
      });
      const total = datasets.reduce((acc, curr) => acc + curr.data[0], 0);

      return {
        labels: ['Lifecycle (Readiness)'],
        datasets: datasets
          .map(v => ({ ...v, data: [((v.data[0] / total) * 10000) / 100], rawData: v.data[0] }))
          .filter(x => x.data[0] > 0),
      };
    }, [readiness]);

    // Add this new function to generate the same colors for dots
    const getLifecycleColor = (key: string) => {
      const lifecycleStates = Object.keys(readiness).filter(k => k !== 'total');
      const baseColors = {
        darkest: { r: 61, g: 90, b: 128 },
        lightest: { r: 137, g: 180, b: 216 },
      };

      const index = lifecycleStates.indexOf(key);
      const ratio = lifecycleStates.length === 1 ? 0 : index / (lifecycleStates.length - 1);

      const r = Math.round(baseColors.darkest.r + (baseColors.lightest.r - baseColors.darkest.r) * ratio);
      const g = Math.round(baseColors.darkest.g + (baseColors.lightest.g - baseColors.darkest.g) * ratio);
      const b = Math.round(baseColors.darkest.b + (baseColors.lightest.b - baseColors.darkest.b) * ratio);

      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    // Create Materiality chart data
    const materialityData = useMemo(() => {
      const datasets: DatasetType[] = [];
      datasets[0] = {
        data: [materiality.low],
        backgroundColor: '#5d9f46e6',
        hoverBackgroundColor: '#5d9f46cc',
        borderSkipped: false,
        datasetIndex: 0,
      };

      datasets[1] = {
        data: [materiality.medium],
        backgroundColor: '#e3ba16e6',
        hoverBackgroundColor: '#e3ba16cc',
        borderSkipped: false,
        datasetIndex: 1,
      };

      datasets[2] = {
        data: [materiality.high],
        backgroundColor: '#c84c3ce6',
        hoverBackgroundColor: '#c84c3ccc',
        borderSkipped: false,
        datasetIndex: 2,
      };

      const materialityValues = [materiality.low, materiality.medium, materiality.high];
      const borderRadiuses = MapperHelper.applyBorderRadius(materialityValues);
      const [lowBorderRadius, mediumBorderRadius, highBorderRadius] = borderRadiuses;
      datasets[0].borderRadius = lowBorderRadius;
      datasets[1].borderRadius = mediumBorderRadius;
      datasets[2].borderRadius = highBorderRadius;
      const total = materialityValues.reduce((acc, curr) => acc + curr, 0);

      return {
        labels: ['Materiality / Criticality'],
        datasets: datasets
          .map(v => ({ ...v, data: [((v.data[0] / total) * 10000) / 100], rawData: v.data[0] }))
          .filter(x => x.data[0] > 0),
      };
    }, [materiality]);

    return (
      <ChartCard
        title="Portfolio"
        subtitle="Overview of all use cases and AI models in the filtered portfolio"
        icon="clipboard"
        iconClassName={styles.icon}
        className={styles.card}
        infoPosition="bottom"
        info={values?.portfolio || ' '}
      >
        <div className={styles.grid}>
          <div>
            <div className={styles.title}>Use cases</div>
            <div className={styles.status}>{`${useCasesTotal} use case${useCasesTotal !== 1 ? 's' : ''}`}</div>
            <div className={styles.chart}>
              <Line
                height={80}
                data={{
                  ...data,
                  datasets: [{ ...data.datasets[0], data: useCases.map(usecase => usecase.useCaseCount) }],
                  labels: useCases.map(usecase => dayjs(usecase.month).format('MMMM YYYY')),
                }}
                options={options}
              />
            </div>
          </div>
          <div>
            <div className={styles.title}>Models</div>
            <div
              className={clsx(styles.status, styles.link, styles.live)}
              onClick={() => push(`${AppRoutes.modelsInventory}/?status=live&redirection=true`)}
            >
              {liveModels} live
            </div>
            <div
              className={clsx(styles.status, styles.link, styles.inReview)}
              onClick={() => push(`${AppRoutes.modelsInventory}/?status=in review&redirection=true`)}
            >
              {inReviewModels} in review
            </div>
            <div
              className={clsx(styles.status, styles.link, styles.notLive)}
              onClick={() => push(`${AppRoutes.modelsInventory}/?status=not live&redirection=true`)}
            >
              {notLiveModels} not live
            </div>
            {(liveModels > 0 || inReviewModels > 0 || notLiveModels > 0) && <StackedPill data={modelsData} />}
          </div>
          <div className={styles.chartWrapper}>
            <div className={styles.title}>EU AI Act Risk Classification</div>
            <div
              className={clsx(styles.status, styles.link, styles.lowRisk)}
              onClick={() =>
                push(
                  `${AppRoutes.usecasesInventory}/?euAiActRiskClass=${ApiUseCasesListEuAiActRiskClassEnum.minimalRisk}&redirection=true`,
                )
              }
            >
              {euAiActRiskClass.minimalRisk} minimal risk
            </div>
            <div
              className={clsx(styles.status, styles.link, styles.mediumRisk)}
              onClick={() =>
                push(
                  `${AppRoutes.usecasesInventory}/?euAiActRiskClass=${ApiUseCasesListEuAiActRiskClassEnum.limitedRisk}&redirection=true`,
                )
              }
            >
              {euAiActRiskClass.limitedRisk} limited risk
            </div>
            <div
              className={clsx(styles.status, styles.link, styles.highRisk)}
              onClick={() =>
                push(
                  `${AppRoutes.usecasesInventory}/?euAiActRiskClass=${ApiUseCasesListEuAiActRiskClassEnum.highRisk}&redirection=true`,
                )
              }
            >
              {euAiActRiskClass.highRisk} high risk
            </div>
            <div
              className={clsx(styles.status, styles.link, styles.veryhighRisk)}
              onClick={() =>
                push(
                  `${AppRoutes.usecasesInventory}/?euAiActRiskClass=${ApiUseCasesListEuAiActRiskClassEnum.prohibitedVeryHighRisk}&redirection=true`,
                )
              }
            >
              {euAiActRiskClass.prohibitedVeryHighRisk} prohibited risk
            </div>
            <div
              className={clsx(styles.status, styles.link, styles.notAssessed)}
              onClick={() =>
                push(
                  `${AppRoutes.usecasesInventory}/?euAiActRiskClass=${ApiUseCasesListEuAiActRiskClassEnum.toBeAssessed}&redirection=true`,
                )
              }
            >
              {euAiActRiskClass.toBeAssessed} to be assessed
            </div>
            <StackedPill data={euAIActRiskData} />
          </div>
          <div className={styles.chartWrapper}>
            <div className={styles.title}>Materiality / Criticality</div>
            <div
              className={clsx(styles.status, styles.link, styles.lowCritical)}
              onClick={() =>
                push(`${AppRoutes.usecasesInventory}/?materiality=${MaterialityEnum.low}&redirection=true`)
              }
            >
              {materiality.low} low critical
            </div>
            <div
              className={clsx(styles.status, styles.link, styles.mediumCritical)}
              onClick={() =>
                push(`${AppRoutes.usecasesInventory}/?materiality=${MaterialityEnum.medium}&redirection=true`)
              }
            >
              {materiality.medium} medium critical
            </div>
            <div
              className={clsx(styles.status, styles.link, styles.highCritical)}
              onClick={() =>
                push(`${AppRoutes.usecasesInventory}/?materiality=${MaterialityEnum.high}&redirection=true`)
              }
            >
              {materiality.high} high critical
            </div>
            <StackedPill data={materialityData} />
          </div>
          <div className={styles.fullWidth}>
            <div className={styles.title}>Lifecycle (Readiness)</div>
            <div className={styles.lifecycleItems}>
              {Object.entries(readiness).map(
                ([key, value]) =>
                  key !== 'total' && (
                    <div
                      key={key}
                      className={clsx(styles.readinessWrapper, styles[key])}
                    >
                      <span
                        className={styles.dot}
                        style={{ backgroundColor: getLifecycleColor(key) }}
                      />
                      {value} {key.toLowerCase()}
                    </div>
                  ),
              )}
            </div>
            <StackedPill data={readinessData} />
          </div>
        </div>
      </ChartCard>
    );
  },
);
